"use client";

import { useEffect, useRef, useCallback } from "react";

interface RichHtmlEditorProps {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
}

export function RichHtmlEditor({ value, onChange, disabled }: RichHtmlEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only on mount

  const exec = useCallback(
    (cmd: string, val?: string) => {
      document.execCommand(cmd, false, val);
      editorRef.current?.focus();
      if (editorRef.current) onChange(editorRef.current.innerHTML);
    },
    [onChange]
  );

  const handleInput = useCallback(() => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  }, [onChange]);

  const btnCls = "px-2 py-1 text-xs rounded hover:bg-accent transition-colors select-none disabled:opacity-50";

  return (
    <div className="border rounded-md overflow-hidden" style={{ borderColor: "hsl(240 20% 22%)" }}>
      <div className="flex flex-wrap gap-0.5 p-1.5 border-b" style={{ background: "hsl(240 20% 14%)", borderColor: "hsl(240 20% 22%)" }}>
        <button type="button" disabled={disabled} className={`${btnCls} font-bold`} onClick={() => exec("bold")}>B</button>
        <button type="button" disabled={disabled} className={`${btnCls} italic`} onClick={() => exec("italic")}>I</button>
        <button type="button" disabled={disabled} className={`${btnCls} underline`} onClick={() => exec("underline")}>U</button>
        <span className="w-px bg-border mx-1" />
        <button type="button" disabled={disabled} className={btnCls} onClick={() => exec("formatBlock", "h2")}>H2</button>
        <button type="button" disabled={disabled} className={btnCls} onClick={() => exec("formatBlock", "h3")}>H3</button>
        <button type="button" disabled={disabled} className={`${btnCls} text-muted-foreground`} onClick={() => exec("formatBlock", "p")}>P</button>
        <span className="w-px bg-border mx-1" />
        <button type="button" disabled={disabled} className={btnCls} onClick={() => exec("insertUnorderedList")}>• List</button>
        <button type="button" disabled={disabled} className={btnCls} onClick={() => exec("insertOrderedList")}>1. List</button>
        <span className="w-px bg-border mx-1" />
        <button type="button" disabled={disabled} className={`${btnCls} text-destructive`} onClick={() => exec("removeFormat")}>Clear</button>
      </div>
      <div
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        className="min-h-[160px] p-3 text-sm focus:outline-none prose prose-sm max-w-none dark:prose-invert"
        onInput={handleInput}
        onBlur={() => { if (editorRef.current) onChange(editorRef.current.innerHTML); }}
        aria-label="Rich text editor"
      />
    </div>
  );
}
